import {
  cn,
  Button,
  Flex,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text,
  type IconType,
} from '@kognitos/lattice';
import { useTranslation } from 'react-i18next';
import {
  WORKSPACE_COLORS,
  WORKSPACE_ICONS,
} from '@/modules/onboarding/constants';

/**
 * Props for the WorkspaceIconPicker component.
 */
interface IWorkspaceIconPickerProps {
  /** Currently selected icon type */
  selectedIcon: IconType;
  /** Currently selected background color (hex string from WORKSPACE_COLORS) */
  selectedColor: string;
  /** Callback fired when the user selects a different icon */
  onIconChange?: (icon: IconType) => void;
  /** Callback fired when the user selects a different color */
  onColorChange?: (color: string) => void;
  /** Disables editing and hides the popover */
  readOnly?: boolean;
  /** Size of the trigger button */
  size?: 'xs' | 'sm' | 'md' | 'lg';
}

/** Tailwind size classes for the trigger button. */
const sizeMap = {
  xs: 'w-8 h-8',
  sm: 'w-10 h-10',
  md: 'w-14 h-14',
  lg: 'w-20 h-20',
};

/**
 * Displays a workspace icon on a colored background with an optional popover
 * for choosing a different icon and color.
 *
 * @param {IWorkspaceIconPickerProps} props - Component props
 * @returns {JSX.Element} The workspace icon picker
 */
export function WorkspaceIconPicker({
  selectedIcon,
  selectedColor,
  onIconChange,
  onColorChange,
  size = 'md',
  readOnly = false,
}: IWorkspaceIconPickerProps) {
  const { t } = useTranslation('workspace');

  const handleIconChange = (icon: IconType) => {
    if (!readOnly && onIconChange) {
      onIconChange(icon);
    }
  };

  const handleColorChange = (color: string) => {
    if (!readOnly && onColorChange) {
      onColorChange(color);
    }
  };

  const triggerContent = (
    <Flex position="relative" className="group">
      <button
        type="button"
        className={cn(
          'flex items-center justify-center rounded-sm p-1 shrink-0',
          sizeMap[size],
          !readOnly && 'cursor-pointer',
          readOnly && 'cursor-default',
        )}
        /* theme-fixed: workspace brand colors are user-chosen and don't vary with theme */
        style={{ backgroundColor: selectedColor }}
        disabled={readOnly}
        data-testid="workspace-icon-picker-trigger"
      >
        <Icon
          type={selectedIcon}
          strokeWidth={1.5} // TODO: strokeWidth doesn't match Figma width due to Icon scaling. Revisit after design team discusses.
          className={cn(
            /* theme-fixed: white needed for contrast on user-chosen brand-color backgrounds */
            'w-full h-full text-white transition-opacity',
            !readOnly && 'group-hover:opacity-20',
          )}
        />
      </button>

      {!readOnly && (
        <Flex
          align="center"
          justify="center"
          className={cn(
            'absolute inset-0 cursor-pointer bg-background/20',
            'rounded-sm opacity-0 group-hover:opacity-100 transition-opacity',
          )}
        >
          <Icon type="PenLine" className="size-5 text-foreground" />
        </Flex>
      )}
    </Flex>
  );

  if (readOnly) {
    return <Flex position="relative">{triggerContent}</Flex>;
  }

  return (
    <Popover>
      <Flex position="relative">
        <PopoverTrigger asChild>{triggerContent}</PopoverTrigger>
      </Flex>

      <PopoverContent className="w-62 p-1" align="start">
        <div className="px-2 py-1.5">
          <Text level="small" className="font-semibold">
            {t('workspace.iconPicker.chooseIconColor')}
          </Text>
        </div>

        {/* Icon Grid */}
        <Flex wrap="wrap" gap={1} padding={2}>
          {WORKSPACE_ICONS.map(iconType => (
            <Button
              key={iconType}
              type="button"
              className={cn(
                'hover:scale-110 transition-transform',
                'flex items-center justify-center h-6 w-6',
                selectedIcon === iconType && 'bg-accent',
              )}
              onClick={() => handleIconChange(iconType)}
              variant="ghost"
              data-testid={`workspace-icon-picker-icon-${iconType}`}
            >
              <Icon
                type={iconType}
                size="sm"
                className="text-muted-foreground"
              />
            </Button>
          ))}
        </Flex>

        {/* Color Palette */}
        <Flex className="gap-1.5 p-2">
          {WORKSPACE_COLORS.map(color => (
            <button
              key={color}
              type="button"
              className={cn(
                'w-4 h-4 rounded-full border-2 hover:scale-110 cursor-pointer',
                'transition-transform flex items-center justify-center',
                selectedColor === color
                  ? 'border-foreground shadow-lg'
                  : 'border-transparent',
              )}
              /* theme-fixed: workspace brand colors are user-chosen and don't vary with theme */
              style={{ backgroundColor: color }}
              onClick={() => handleColorChange(color)}
              data-testid={`workspace-icon-picker-color-${color}`}
            >
              {selectedColor === color && (
                /* theme-fixed: white needed for contrast on user-chosen brand-color backgrounds */
                <Icon type="Check" size={12} className="text-white" />
              )}
            </button>
          ))}
        </Flex>
      </PopoverContent>
    </Popover>
  );
}
